import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import type { ImageGalleryDialogRef } from '#ui/image-gallery-dialog.js';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { AppImage } from '#ui/AppImage.js';
import { ImageGalleryDialog } from '#ui/image-gallery-dialog.js';
import { detectImageMimeType, IconSizes, MaterialIcon } from '#ui/index.js';

const AUTOPLAY_INTERVAL_MS = 4000;

export interface ImageGalleryWidgetProps {
  images: WidgetImage[];
  className?: string;
  layout?: 'grid' | 'inline';
  layoutOrientation?: 'portrait' | 'landscape';
  round?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  aspect?: 'square' | 'video' | 'auto' | 'header' | 'fill';
  gap?: number;
  maxImages?: number; // Optional limit on how many images to display in the card
  offset?: number; // Optional offset to start displaying images from
  displayCount?: boolean; // Whether to display the number of images in the card
  interactive?: boolean;
  /** When true and maxImages is 1, cycle through images with a fade (default: false) */
  autoplay?: boolean;
  /**
   * Replaces the lightbox: given this, a click on a photo calls it instead of opening the viewer.
   *
   * For a gallery that is the cover of a CARD. There the photos are not the content, they are a
   * picture of the thing the card is about, so a click on one should go where every other part of the
   * card goes — its guide, which carries the full gallery anyway. A lightbox there made the photos the
   * one part of a card that did something different from the rest of it.
   */
  onImageClick?: (event: React.MouseEvent) => void;
  /** Whether the lightbox offers "Ask Elara". Off where there is no trip chat (the public shared itinerary). */
  canAskElara?: boolean;
}

// Reusable constants
const CARD_BASE_CLASSES = 'overflow-hidden cursor-pointer transition-all group';

/**
 * ImageGalleryWidget displays images in a grid layout or inline horizontal layout.
 * When an image is clicked, it opens the ImageGalleryDialog for full-screen viewing.
 *
 * @param maxImages - Optional limit on images displayed in card (all images still accessible via gallery)
 * @param inline - Display images inline in a horizontal scrollable layout instead of grid
 */
export function ImageGalleryWidget({
  images,
  className,
  layout = 'grid',
  layoutOrientation = 'landscape',
  round = 'lg', // 'none', 'sm', 'md', 'lg', 'xl'
  maxImages = 5,
  offset = 0,
  aspect = 'video',
  gap = 1,
  displayCount = false,
  interactive = true,
  autoplay = false,
  onImageClick,
  canAskElara = true,
}: ImageGalleryWidgetProps) {
  const galleryRef = useRef<ImageGalleryDialogRef | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [autoplayIndex, setAutoplayIndex] = useState(0);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const isInline = layout === 'inline';

  let aspectClass = `aspect-${aspect}`;

  if (aspect === 'header') {
    aspectClass = 'aspect-2/1 md:aspect-5/2';
  }

  if (aspect === 'fill') {
    aspectClass = 'h-full w-full';
  }

  const handleImageError = (imageId: string) => {
    console.error('[ImageGalleryWidget] Image failed to load:', imageId);
    setFailedImages((prev) => new Set(prev).add(imageId));
  };


  // Compute before any early return so hooks always run.
  //
  // A url that 404s is DROPPED rather than shown as a "Failed to load" tile. Supplier photo CDNs rot —
  // seven of the fifteen urls on the seeded rooms are dead — and a grid of six with two apology tiles
  // in it reads as a broken page, where a grid of four reads as a room with four photos. Nothing is
  // lost: the tile carried no information beyond "this is missing".
  //
  // Filtered BEFORE the `maxImages` slice, the opposite order to the card carousels. There the cap
  // means "how many of this entity's photos belong on a card", so reaching past it would page onto
  // photos the guide exists to show. Here the count is a LAYOUT — one large tile beside a 2×2 — and a
  // hole in it is the thing being fixed, so the remaining images backfill.
  const liveImages = images && images.length > 0 ? images.filter((image) => !failedImages.has(image.id)) : [];
  const offsetImages = liveImages.slice(offset);
  const displayedImages = offsetImages.slice(0, maxImages);
  const isAutoplayCarousel = autoplay && maxImages === 1 && offsetImages.length > 1;

  // Observe visibility so we only animate when the gallery is in view
  useEffect(() => {
    if (!isAutoplayCarousel || !containerRef.current) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setIsGalleryVisible(entry.isIntersecting);
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAutoplayCarousel]);

  // Track inline scroll overflow to show/hide arrows
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 2;
    setCanScrollLeft(el.scrollLeft > threshold);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - threshold);
  }, []);

  useEffect(() => {
    if (!isInline) return;
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [isInline, updateScrollState]);

  const scrollInline = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstChild = el.firstElementChild as HTMLElement | null;
    if (!firstChild) return;
    const imageWidth = firstChild.offsetWidth + 8; // 8px = gap-2
    el.scrollBy({ left: direction === 'left' ? -imageWidth : imageWidth, behavior: 'smooth' });
  };

  // Autoplay: cycle through images when showing a single slot and gallery is visible
  useEffect(() => {
    if (!isAutoplayCarousel || !isGalleryVisible) return;
    const id = setInterval(() => {
      setAutoplayIndex((i) => (i + 1) % offsetImages.length);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAutoplayCarousel, isGalleryVisible, offsetImages.length]);

  // Validate image count
  if (offsetImages.length === 0) {
    console.warn('[ImageGalleryWidget] No images provided');
    return undefined;
  }

  // Validate displayed image count
  if (!isInline && displayedImages.length > 6) {
    console.warn('[ImageGalleryWidget] Too many images for grid layout:', displayedImages.length);
    return undefined;
  }

  // Every image the entity has — NOT just the `maxImages` the grid shows, which is the point of the
  // lightbox — but only the ones that load. A dead url reaching the lightbox is worse than in the grid:
  // there it was one tile among several, here it is the whole screen, and paging onto it looks like the
  // viewer itself has broken.
  const dialogImages = liveImages.map((img) => ({
    id: img.id,
    name: img.caption,
    attribution: img.attribution,
    source: img.source,
    type: 'image',
    mimeType: detectImageMimeType(img.url),
    url: img.url,
  }));

  const handleImageClick = (image: WidgetImage) => {
    const dialogImage = dialogImages.find((di) => di.id === image.id);
    if (dialogImage) {
      galleryRef.current?.openImage(dialogImage);
    }
  };

  // Render a single image card
  const renderImageCard = (
    image: WidgetImage,
    index: number,
    displayCount?: boolean,
    displayedCount?: number,
    totalCount?: number,
  ) => (
    // A real `button`, not a div with a handler: it opens the lightbox, so assistive tech and the
    // keyboard should both be able to reach it — and a card that opens a guide from its own surface
    // decides what to ignore by looking for an interactive ancestor, which a bare div is not.
    // `stopPropagation` as well, so the photo's click never reaches that surface.
    <button
      type="button"
      key={image.id}
      className={cn(
        CARD_BASE_CLASSES,
        `rounded-${round}`,
        interactive && 'hover:shadow-md',
        isInline ? aspectClass : 'aspect-auto',
        isInline && `flex-shrink-0`,
        isInline ? 'h-32' : imageCardClasses(displayedCount)[index],
      )}
      onClick={(event) => {
        event.stopPropagation();
        if (onImageClick) {
          onImageClick(event);
          return;
        }
        handleImageClick(image);
      }}
    >
      <div className={cn('relative h-full w-full')}>
        <AppImage
          src={image.url}
          alt={image.alt || image.caption || `Gallery image ${index + 1}`}
          className="w-full h-full object-cover bg-background-quaternary"
          onAllFailed={() => handleImageError(image.id)}
        />
        {(
          <div
            className={cn(
              'absolute inset-0 bg-black/40 opacity-0 transition-opacity',
              interactive && 'group-hover:opacity-100',
            )}
          />
        )}

        {displayCount && totalCount && (
          <div className="absolute bottom-2 right-2 rounded-sm backdrop-blur-sm text-white bg-black/40 transition-opacity flex items-center justify-center gap-1 px-1.5 py-1">
            <MaterialIcon icon="image" size={IconSizes.sm} />
            <span className="text-sm">{totalCount}</span>
          </div>
        )}
      </div>
    </button>
  );

  const imageCardClasses = (count?: number) => {
    switch (count) {
      case 1: {
        return layoutOrientation === 'landscape' ? ['col-span-2 row-span-1'] : ['col-span-1 row-span-2'];
      }
      case 2: {
        return ['col-span-1 row-span-1', 'col-span-1 row-span-1'];
      }
      case 3: {
        return layoutOrientation === 'landscape'
          ? ['col-span-2 row-span-2', 'col-span-2 row-span-1', 'col-span-2 row-span-1']
          : ['col-span-2 row-span-1', 'col-span-1 row-span-1', 'col-span-1 row-span-1'];
      }
      case 4: {
        return layoutOrientation === 'landscape'
          ? ['col-span-2 row-span-2', 'col-span-2 row-span-1', 'col-span-1 row-span-1', 'col-span-1 row-span-1']
          : ['col-span-3 row-span-2', 'col-span-1 row-span-1', 'col-span-1 row-span-1', 'col-span-1 row-span-1'];
      }
      default: {
        if (layoutOrientation === 'landscape') {
          return [
            'col-span-2 row-span-2',
            'col-span-1 row-span-1',
            'col-span-1 row-span-1',
            'col-span-1 row-span-1',
            'col-span-1 row-span-1',
          ];
        } else {
          return [
            'col-span-4 row-span-3',
            'col-span-1 row-span-1',
            'col-span-1 row-span-1',
            'col-span-1 row-span-1',
            'col-span-1 row-span-1',
          ];
        }
      }
    }
  };

  // Get grid container classes based on image count
  const getGridClasses = (count: number) => {
    if (isInline) {
      return 'flex gap-2 h-full overflow-x-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent';
    }

    switch (count) {
      case 1: {
        return layoutOrientation === 'landscape'
          ? `grid grid-cols-2 grid-rows-1 gap-${gap}`
          : `grid grid-cols-1 grid-rows-2 gap-${gap}`;
      }
      case 2: {
        return layoutOrientation === 'landscape'
          ? `grid grid-cols-2 grid-rows-1 gap-${gap}`
          : `grid grid-cols-1 grid-rows-2 gap-${gap}`;
      }
      case 3: {
        return layoutOrientation === 'landscape'
          ? `grid grid-cols-[1fr_1fr_1fr_1fr] grid-rows-[2fr_2fr] gap-${gap}`
          : `grid grid-cols-[1fr_1fr] grid-rows-[2fr_1fr] gap-${gap}`;
      }
      case 4: {
        return layoutOrientation === 'landscape'
          ? `grid grid-cols-4 grid-rows-2 gap-${gap}`
          : `grid grid-cols-3 grid-rows-3 gap-${gap}`;
      }
      case 5: {
        return layoutOrientation === 'landscape'
          ? `grid grid-cols-4 grid-rows-2 gap-${gap}`
          : `grid grid-cols-4 grid-rows-4 gap-${gap}`;
      }
      default: {
        return layoutOrientation === 'landscape'
          ? `grid grid-cols-1 grid-rows-1 gap-${gap}`
          : `grid grid-cols-1 grid-rows-1 gap-${gap}`;
      }
    }
  };

  // Autoplay carousel: single slot with fading image cycle
  if (isAutoplayCarousel) {
    const carouselClasses = getGridClasses(1);
    const cardClass = imageCardClasses(1)[0];
    const safeIndex = autoplayIndex % offsetImages.length;
    const currentImage = offsetImages[safeIndex];
    return (
      <div
        ref={containerRef}
        className={cn('w-full @container/image-gallery', aspect === 'fill' && 'h-full', className)}
      >
        <div className={cn('w-full', !isInline && aspectClass, carouselClasses)}>
          <div
            className={cn(
              CARD_BASE_CLASSES,
              `rounded-${round}`,
              interactive && 'hover:shadow-md',
              isInline ? aspectClass : 'aspect-auto',
              cardClass,
            )}
            onClick={() => currentImage && handleImageClick(currentImage)}
          >
            <div className="relative h-full w-full overflow-hidden">
              {offsetImages.map((image, index) => {
                // Only render current and adjacent images to reduce DOM size
                const distance = Math.abs(index - safeIndex);
                const wrappedDistance = Math.min(distance, offsetImages.length - distance);
                if (wrappedDistance > 1) return null;

                return (
                  <div
                    key={image.id}
                    className={cn(
                      'absolute inset-0 transition-opacity duration-500 ease-in-out',
                      index === safeIndex ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden={index !== safeIndex}
                  >
                    {(
                      <div
                        className={cn('h-full w-full', index % 2 === 0 ? 'kenburns-pan-left' : 'kenburns-pan-right')}
                        style={{ animationPlayState: isGalleryVisible ? 'running' : 'paused' }}
                      >
                        <AppImage
                          src={image.url}
                          alt={image.alt || image.caption || `Gallery image ${index + 1}`}
                          className="w-full h-full object-cover"
                          onAllFailed={() => handleImageError(image.id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <div
                className={cn(
                  'absolute inset-0 bg-black/40 opacity-0 transition-opacity',
                  interactive && 'group-hover:opacity-100',
                )}
              />
              {currentImage?.caption || currentImage?.alt ? (
                <div
                  className={cn(
                    'absolute bottom-0 left-0 right-0 p-6 text-white text-sm opacity-0 transition-opacity truncate',
                    interactive && 'group-hover:opacity-100',
                    'bg-gradient-to-t from-black/70 to-transparent',
                  )}
                  title={(currentImage.caption || currentImage.alt) ?? undefined}
                >
                  <div className="text-base font-medium">{currentImage.caption || currentImage.alt}</div>
                  <div className="text-sm opacity-70">
                    {currentImage.attribution} {currentImage.source ? `via ${currentImage.source}` : ''}
                  </div>
                </div>
              ) : null}
              {displayCount && (
                <div className="absolute bottom-2 right-2 rounded-sm backdrop-blur-sm text-white bg-black/40 transition-opacity flex items-center justify-center gap-1 px-1.5 py-1">
                  <MaterialIcon icon="image" size={IconSizes.sm} />
                  <span className="text-sm">{liveImages.length}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <ImageGalleryDialog
          ref={galleryRef}
          images={dialogImages}
          showCaptions={images.some((img) => img.caption || img.attribution)}
          canAskElara={canAskElara}
        />
      </div>
    );
  }

  // Regular grid or inline layout
  const containerClasses = getGridClasses(displayedImages.length);

  return (
    <div
      className={cn(
        'w-full @container/image-gallery',
        aspect === 'fill' && 'h-full',
        isInline && `rounded-${round} overflow-hidden`,
        className,
      )}
    >
      {isInline ? (
        <div className="relative">
          <div ref={scrollRef} className={cn('w-full', containerClasses)}>
            {displayedImages.map((image, index) =>
              renderImageCard(
                image,
                index,
                displayCount && index === displayedImages.length - 1,
                displayedImages.length,
                liveImages.length,
              ),
            )}
          </div>
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollInline('left')}
              className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center bg-gradient-to-r from-black/60 to-transparent text-white cursor-pointer"
              aria-label="Scroll left"
            >
              <div className="ml-2 h-8 w-8 rounded-full flex items-center justify-center bg-background/30 backdrop-blur-sm hover:bg-background/50 shadow-sm">
                <MaterialIcon icon="chevron_left" size={IconSizes.md} />
              </div>
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollInline('right')}
              className="absolute right-0 top-0 bottom-0 w-10 flex items-center justify-center bg-gradient-to-l from-black/60 to-transparent text-white cursor-pointer"
              aria-label="Scroll right"
            >
              <div className="mr-2 h-8 w-8 rounded-full flex items-center justify-center bg-background/30 backdrop-blur-sm hover:bg-background/50 shadow-sm">
                <MaterialIcon icon="chevron_right" size={IconSizes.md} />
              </div>
            </button>
          )}
        </div>
      ) : (
        <div className={cn('w-full', aspectClass, containerClasses)}>
          {displayedImages.map((image, index) =>
            renderImageCard(
              image,
              index,
              displayCount && index === displayedImages.length - 1,
              displayedImages.length,
              liveImages.length,
            ),
          )}
        </div>
      )}
      <ImageGalleryDialog
        ref={galleryRef}
        images={dialogImages}
        showCaptions={images.some((img) => img.caption || img.attribution)}
        canAskElara={canAskElara}
      />
    </div>
  );
}
